import { AfterViewInit, Component, EventEmitter, OnInit } from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';

import { BsModalRef } from 'ngx-bootstrap/modal';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs';
import { deburr } from 'lodash';

import { LoaderService } from '../../../../services/loader/loader.service';

@Component({
  selector: 'app-modal-select-more',
  templateUrl: './modal-select-more.component.html',
  styleUrls: ['./modal-select-more.component.scss']
})
export class ModalSelectMoreComponent implements OnInit, AfterViewInit {

  title?: string;
  subtitle?: string;
  type?: 'structures' | 'usages';

  searchForm;
  itemsForm;
  filteredList = [];
  isSearching = false;

  allCheckboxForm; // for all checkboxes visible
  newAllCheckboxForm = new FormControl(false); // for a subset of checkboxes visible

  onClose: EventEmitter<any> = new EventEmitter();

  constructor(
    public bsModalRef: BsModalRef,
    public readonly loaderService: LoaderService,
  ) {}

  ngOnInit() {
    // init event to refresh all checkboxes selector status
    this.itemsForm.valueChanges.pipe(debounceTime(250)).subscribe(values => this.refreshSubsetAllCheckboxesStatus(values));

    // init event to check or uncheck the subset of checkboxes
    this.newAllCheckboxForm.valueChanges.subscribe(isChecked => {
      const checkboxes = this.itemsForm as FormArray<FormControl>;
      Object
        .entries(checkboxes.controls)
        .filter((checkbox) => this.filteredList.includes(checkbox[0]))
        .forEach(control => control[1].patchValue(isChecked, { emitEvent: true }));
    });

    // init search form
    this.searchForm = new FormControl('');
    this.searchForm.valueChanges
      .pipe(
        tap(() => {
          this.loaderService.show();
          this.isSearching = true;
        }),
        distinctUntilChanged(),
        debounceTime(500),
        tap(() => {
          this.filteredList = [];
        }),
      )
      .subscribe({
        next: term => {
          // ignore accented characters and case sensitive
          const deburredSubString = deburr(term).toLowerCase();
          this.filteredList = Object
            .keys(this.itemsForm.value)
            .filter(x => deburr(x).toLowerCase().includes(deburredSubString));

          // refresh all checkboxes selector status
          this.refreshSubsetAllCheckboxesStatus(this.itemsForm.value);

          this.loaderService.hide();
          this.isSearching = false;
        }
      });
  }

  ngAfterViewInit(): void {
    // workaround to reset the focus drag zone after a scroll
    setTimeout(() => this.scroll(null), 500);
  }

  /**
   * Refresh "Select all checkboxes" selector status for a subset at search
   * @param values Checkboxes values
   */
  private refreshSubsetAllCheckboxesStatus(values) {
    if (this.filteredList.length > 0) {
      const isAllChecked = Object
        .entries(values)
        .filter(x => this.filteredList.includes(x[0]))
        .every(x => x[1]);
      this.newAllCheckboxForm.setValue(isAllChecked, { emitEvent: false });
    }
  }

  /**
   * Filter and close form
   */
  public filter() {
    this.onClose.next('filter');
    this.bsModalRef.hide();
  }

  /**
   * Reset form and close popin
   */
  public reinitialize() {
    this.onClose.next('reset');
    this.bsModalRef.hide();
  }

  /*** DRAG SELECTION ***/

  /**
   * Detect all filter bar scroll events
   * @param event
   */
  scroll(event: any) {
    // workaround to reset the focus drag zone after a scroll
    window.scrollTo(window.scrollX, window.scrollY + 10);
    setTimeout(() => window.scrollTo(window.scrollX, window.scrollY - 10), 10);
  }

  /**
   * Handle drag selection
   * @param selectedItems Selected items list
   * @param target List to target
   */
  public handleDragSelection(selectedItems: Array<any>, target: 'structures' | 'usages'): void {
    // ignore single click trigger event for single elements
    if (selectedItems.length <= 1) {
      return;
    }

    const selectedNames = selectedItems.map(x => x.key);
    const value = !selectedItems
      .map(x => x.value)
      .every(x => x === true);

    const checkboxes = this.itemsForm as FormArray<FormControl>;
    Object
      .entries(checkboxes.controls)
      .filter((checkbox, index) => selectedNames.includes(checkbox[0]))
      .forEach(control => control[1].patchValue(value, { emitEvent: true }));
  }

}
